import { domReady } from '../helpers';

export function projectsPageFunc() {

	// ── DOM refs ────────────────────────────────────────────────────────────
	const filterBar   = document.getElementById( 'projects-filter' );
	const pillsWrap   = document.getElementById( 'filter-pills' );
	const resultsWrap = document.getElementById( 'projects-results' );
	const resultsCount = document.getElementById( 'projects-results-count' );

	if ( ! filterBar || ! resultsWrap ) return;

	// ── State ────────────────────────────────────────────────────────────────
	// Keyed by taxonomy slug, value is a Set of term slugs
	const activeFilters = {
		'work-type'     : new Set(),
		'roof-style'    : new Set(),
		'material-type' : new Set(),
	};

	let currentPage = 1;
	let isFetching  = false;

	// WordPress static pages use /page/N/ in the path; we use ?paged=N for filters.
	function getBasePath() {
		return window.location.pathname
			.replace( /\/page\/\d+\/?$/, '' )
			.replace( /\/?$/, '/' ) || '/';
	}

	function getPageFromURL() {
		const params    = new URLSearchParams( window.location.search );
		const fromQuery = parseInt( params.get( 'paged' ), 10 );
		if ( fromQuery > 0 ) return fromQuery;

		const match = window.location.pathname.match( /\/page\/(\d+)\/?$/ );
		if ( match ) return parseInt( match[1], 10 );

		return 1;
	}

	// ── Hydrate state from URL on load ───────────────────────────────────────
	function hydrateFromURL() {
		const params = new URLSearchParams( window.location.search );

		[ 'work-type', 'roof-style', 'material-type' ].forEach( tax => {
			const values = params.getAll( tax + '[]' );
			values.forEach( v => activeFilters[ tax ].add( v ) );
		} );

		currentPage = getPageFromURL();

		// Reflect hydrated state onto checkboxes
		document.querySelectorAll( '.filter-dropdown__checkbox' ).forEach( cb => {
			const tax = cb.dataset.taxonomy;
			if ( activeFilters[ tax ] && activeFilters[ tax ].has( cb.value ) ) {
				cb.checked = true;
			}
		} );
	}

	// ── URL sync ─────────────────────────────────────────────────────────────
	function buildURLString() {
		const params = new URLSearchParams();

		Object.entries( activeFilters ).forEach( ( [ tax, slugs ] ) => {
			slugs.forEach( slug => params.append( tax + '[]', slug ) );
		} );

		if ( currentPage > 1 ) params.set( 'paged', currentPage );

		return getBasePath() + ( params.toString() ? '?' + params.toString() : '' );
	}

	function updateURL() {
		window.history.pushState( {}, '', buildURLString() );
	}

	function getRenderedPage() {
		const current = resultsWrap.querySelector( '.page-numbers.current' );
		if ( current ) {
			const page = parseInt( current.textContent, 10 );
			return page > 0 ? page : 1;
		}
		return 1;
	}

	// ── Active pills ─────────────────────────────────────────────────────────
	function renderPills() {
		pillsWrap.innerHTML = '';

		let hasAny = false;

		Object.entries( activeFilters ).forEach( ( [ tax, slugs ] ) => {
			slugs.forEach( slug => {
				hasAny = true;

				// Find the human label from the matching checkbox
				const cb = filterBar.querySelector(
					`.filter-dropdown__checkbox[data-taxonomy="${ tax }"][value="${ slug }"]`
				);
				const label = cb ? cb.dataset.label : slug;

				const pill = document.createElement( 'button' );
				pill.type  = 'button';
				pill.className  = 'filter-pill';
				pill.dataset.taxonomy = tax;
				pill.dataset.slug     = slug;
				pill.innerHTML  = `<span class="filter-pill__label">${ label }</span>
					<span class="filter-pill__remove" aria-hidden="true">×</span>`;
				pill.setAttribute( 'aria-label', `Remove filter: ${ label }` );

				pill.addEventListener( 'click', () => {
					removeTerm( tax, slug );
				} );

				pillsWrap.appendChild( pill );
			} );
		} );

		// ── Clear all ──
		if ( hasAny ) {
			const clearAll = document.createElement( 'button' );
			clearAll.type      = 'button';
			clearAll.className = 'filter-pill filter-pill--clear-all';
			clearAll.textContent = 'Clear all';
			clearAll.setAttribute( 'aria-label', 'Clear all filters' );

			clearAll.addEventListener( 'click', () => {
				Object.keys( activeFilters ).forEach( tax => activeFilters[ tax ].clear() );

				document.querySelectorAll( '.filter-dropdown__checkbox' ).forEach( cb => {
					cb.checked = false;
				} );

				document.querySelectorAll( '.filter-dropdown' ).forEach( dd => {
					dd.classList.remove( 'filter-dropdown--has-active' );
				} );

				currentPage = 1;
				renderPills();
				updateURL();
				fetchResults();
			} );

			pillsWrap.appendChild( clearAll );
		}
		
		pillsWrap.hidden = ! hasAny;
	}

	// ── Dropdown open/close ───────────────────────────────────────────────────
	function closeAllDropdowns( except ) {
		document.querySelectorAll( '.filter-dropdown' ).forEach( dd => {
			if ( dd === except ) return;
			const trigger = dd.querySelector( '.filter-dropdown__trigger' );
			const panel   = dd.querySelector( '.filter-dropdown__panel' );
			trigger.setAttribute( 'aria-expanded', 'false' );
			panel.hidden = true;
			dd.classList.remove( 'filter-dropdown--open' );
		} );
	}

	document.querySelectorAll( '.filter-dropdown__trigger' ).forEach( trigger => {
		trigger.addEventListener( 'click', () => {
			const dropdown = trigger.closest( '.filter-dropdown' );
			const panel    = dropdown.querySelector( '.filter-dropdown__panel' );
			const isOpen   = trigger.getAttribute( 'aria-expanded' ) === 'true';

			closeAllDropdowns( isOpen ? null : dropdown );

			if ( ! isOpen ) {
				trigger.setAttribute( 'aria-expanded', 'true' );
				panel.hidden = false;
				dropdown.classList.add( 'filter-dropdown--open' );
			} else {
				trigger.setAttribute( 'aria-expanded', 'false' );
				panel.hidden = true;
				dropdown.classList.remove( 'filter-dropdown--open' );
			}
		} );
	} );

	// Close dropdowns when clicking outside
	document.addEventListener( 'click', e => {
		if ( ! e.target.closest( '.filter-dropdown' ) ) {
			closeAllDropdowns();
		}
	} );

	// ── Checkbox change ───────────────────────────────────────────────────────
	filterBar.addEventListener( 'change', e => {
		const cb = e.target;
		if ( ! cb.classList.contains( 'filter-dropdown__checkbox' ) ) return;

		const tax  = cb.dataset.taxonomy;
		const slug = cb.value;

		if ( cb.checked ) {
			activeFilters[ tax ].add( slug );
		} else {
			activeFilters[ tax ].delete( slug );
		}

		// Update dropdown "has active" class
		const dropdown = cb.closest( '.filter-dropdown' );
		dropdown.classList.toggle(
			'filter-dropdown--has-active',
			activeFilters[ tax ].size > 0
		);

		currentPage = 1;
		renderPills();
		updateURL();
		fetchResults();
	} );

	// ── Remove a term (via pill or programmatically) ──────────────────────────
	function removeTerm( tax, slug ) {
		activeFilters[ tax ].delete( slug );

		// Uncheck the matching checkbox
		const cb = filterBar.querySelector(
			`.filter-dropdown__checkbox[data-taxonomy="${ tax }"][value="${ slug }"]`
		);
		if ( cb ) cb.checked = false;

		// Update dropdown "has active" class
		const dropdown = cb ? cb.closest( '.filter-dropdown' ) : null;
		if ( dropdown ) {
			dropdown.classList.toggle(
				'filter-dropdown--has-active',
				activeFilters[ tax ].size > 0
			);
		}

		currentPage = 1;
		renderPills();
		updateURL();
		fetchResults();
	}

	// ── Pagination (event delegation on results wrapper) ─────────────────────
	resultsWrap.addEventListener( 'click', e => {
		const link = e.target.closest( 'a.page-numbers' );
		if ( ! link ) return;

		e.preventDefault();

		const page = parseInt( link.dataset.page, 10 );
		if ( ! page ) return;

		if ( page === currentPage ) {
			if ( window.lenis ) {
				window.lenis.scrollTo( '#projects-filter' );
			}
			return;
		}

		currentPage = page;
		updateURL();
		fetchResults();

		if ( window.lenis ) {
			window.lenis.scrollTo( '#projects-filter' );
		}
	} );

	// ── AJAX fetch ────────────────────────────────────────────────────────────
	function buildFormData() {
		const fd = new FormData();
		fd.append( 'action', 'filter_projects' );
		fd.append( 'nonce',  ProjectsData.nonce );
		fd.append( 'paged',  currentPage );
		fd.append( 'customer_type', ProjectsData.customerType ?? '' );

		Object.entries( activeFilters ).forEach( ( [ tax, slugs ] ) => {
			slugs.forEach( slug => fd.append( tax + '[]', slug ) );
		} );

		return fd;
	}

	function fetchResults() {
		if ( isFetching ) return;
		isFetching = true;

		// Fade out current results
		if ( typeof gsap !== 'undefined' ) {
			gsap.to( resultsWrap, {
				opacity  : 0.3,
				duration : 0.2,
				ease     : 'power1.out',
			} );
		}

		fetch( ProjectsData.ajaxUrl, {
			method : 'POST',
			body   : buildFormData(),
		} )
			.then( r => r.json() )
			.then( response => {
				if ( ! response.success ) return;

				resultsWrap.innerHTML = response.data.html;
				
				if ( resultsCount ) {
					const total   = response.data.found_posts;
					const perPage = ProjectsData.projectsPerPage || 6;
					const start = total > 0 ? ( ( currentPage - 1 ) * perPage ) + 1 : 0;
					const end   = Math.min( currentPage * perPage, total );
					resultsCount.textContent = total > 0
							? `Showing ${ start }–${ end } of ${ total } projects`
							: '';
				}

				// Fade results back in
				if ( typeof gsap !== 'undefined' ) {
					gsap.fromTo( resultsWrap,
						{ opacity: 0, y: 12 },
						{ opacity: 1, y: 0, duration: 0.35, ease: 'power2.out' }
					);

					// Stagger the cards
					const cards = resultsWrap.querySelectorAll( '.project-card' );
					gsap.fromTo( cards,
						{ opacity: 0, y: 20 },
						{
							opacity  : 1,
							y        : 0,
							duration : 0.4,
							stagger  : 0.08,
							ease     : 'power2.out',
							delay    : 0.1,
						}
					);
				}
			} )
			.catch( err => console.error( 'Projects filter AJAX error:', err ) )
			.finally( () => {
				isFetching = false;
			} );
	}

	// ── Browser back/forward support ─────────────────────────────────────────
	window.addEventListener( 'popstate', () => {
		// Reset state
		Object.keys( activeFilters ).forEach( tax => activeFilters[ tax ].clear() );
		document.querySelectorAll( '.filter-dropdown__checkbox' ).forEach( cb => {
			cb.checked = false;
		} );
		document.querySelectorAll( '.filter-dropdown' ).forEach( dd => {
			dd.classList.remove( 'filter-dropdown--has-active' );
		} );

		hydrateFromURL();
		renderPills();
		fetchResults();
	} );

	// ── Init ─────────────────────────────────────────────────────────────────
	hydrateFromURL();
	renderPills();

	// Normalize WP /page/N/ paths to ?paged=N on load (e.g. after refresh)
	if ( /\/page\/\d+\/?$/.test( window.location.pathname ) ) {
		window.history.replaceState( {}, '', buildURLString() );
	}

	// Re-sync grid + pagination when reloading with URL state
	const hasUrlState = currentPage > 1 ||
		Object.values( activeFilters ).some( set => set.size > 0 );

	if ( currentPage > 1 || ( hasUrlState && currentPage !== getRenderedPage() ) ) {
		fetchResults();
	}

	// Initial card entrance animation (skip when fetching — AJAX handler animates in)
	if ( ! isFetching && typeof gsap !== 'undefined' ) {
		gsap.fromTo(
			resultsWrap.querySelectorAll( '.project-card' ),
			{ opacity: 0, y: 20 },
			{
				opacity  : 1,
				y        : 0,
				duration : 0.4,
				stagger  : 0.08,
				ease     : 'power2.out',
			}
		);
	}

};

domReady(() => {
	projectsPageFunc();
});
